import React, { useEffect, useState } from 'react';
import { Link as Anchor } from 'react-router-dom';
import baseURL from '../../Components/url';
import { guardarSiteConfig, obtenerNombreTiendaGuardado } from '../../Components/siteConfig';
import { getProductMoneda } from '../../Components/moneda';
import './MiCuenta.css';

const initialRegisterState = {
    nombre: '',
    email: '',
    contrasena: '',
};

const orderStatusLabels = {
    Paid: 'Pagado',
    Shipped: 'Enviado',
    Received: 'Recibido',
    Delivered: 'Entregado',
};

export default function MiCuenta() {
    const [storeName, setStoreName] = useState(() => obtenerNombreTiendaGuardado());
    const [activeTab, setActiveTab] = useState('login');
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [registerForm, setRegisterForm] = useState(initialRegisterState);
    const [profileForm, setProfileForm] = useState({
        nombre: '',
        telefono: '',
        direccion: '',
    });
    const [customerOrders, setCustomerOrders] = useState([]);
    const [feedback, setFeedback] = useState({ type: '', text: '' });

    const showFeedback = (type, text) => {
        setFeedback({ type, text });
    };

    const getOrderStatusLabel = (status) => orderStatusLabels[status] || status || 'Pedido';
    const formatMoney = (value) => `${getProductMoneda()} ${`${Number(value || 0)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
    const getOrderProducts = (pedido) => {
        try {
            const products = JSON.parse(pedido?.productos || '[]');
            return Array.isArray(products) ? products : [];
        } catch (error) {
            return [];
        }
    };

    const loadSessionUser = async () => {
        try {
            const response = await fetch(`${baseURL}/userLogued.php?ts=${Date.now()}`, { cache: 'no-store' });
            const data = await response.json();
            if (data?.idUsuario && data.rol !== 'admin') {
                setUser(data);
                setProfileForm({
                    nombre: data.nombre || '',
                    telefono: data.telefono || '',
                    direccion: data.direccion || '',
                });
                try {
                    const ordersResponse = await fetch(`${baseURL}/customerOrdersGet.php?ts=${Date.now()}`, { cache: 'no-store' });
                    const ordersData = await ordersResponse.json();
                    setCustomerOrders(Array.isArray(ordersData.pedidos) ? ordersData.pedidos : []);
                } catch (error) {
                    console.error('Error al cargar pedidos del cliente:', error);
                    setCustomerOrders([]);
                }
                return;
            }
        } catch (error) {
            console.error('Error al cargar la sesion del cliente:', error);
        }

        setUser(null);
        setCustomerOrders([]);
    };

    useEffect(() => {
        const loadPage = async () => {
            try {
                const response = await fetch(`${baseURL}/configGet.php?ts=${Date.now()}`, { cache: 'no-store' });
                const data = await response.json();
                if (data.config) {
                    guardarSiteConfig(data.config);
                    setStoreName(data.config?.nombreTienda?.trim() || '');
                }
            } catch (error) {
                console.error('Error al cargar la configuracion de mi cuenta:', error);
            }

            await loadSessionUser();
            setLoading(false);
        };

        loadPage();
    }, []);

    const handleCustomerLogin = async (event) => {
        event.preventDefault();

        try {
            const formData = new FormData();
            formData.append('email', loginEmail);
            formData.append('contrasena', loginPassword);

            const response = await fetch(`${baseURL}/login.php`, {
                method: 'POST',
                body: formData,
            });
            const data = await response.json();

            if (data.error) {
                showFeedback('error', data.error);
                return;
            }

            if (data.usuario?.rol === 'admin') {
                showFeedback('info', 'Esta cuenta es administradora. Usa el acceso del dashboard.');
                return;
            }

            showFeedback('success', data.mensaje || 'Sesion iniciada correctamente');
            setLoginPassword('');
            await loadSessionUser();
        } catch (error) {
            console.error('Error al iniciar sesion del cliente:', error);
            showFeedback('error', 'No se pudo iniciar sesion.');
        }
    };

    const handleCustomerRegister = async (event) => {
        event.preventDefault();

        try {
            const formData = new FormData();
            formData.append('nombre', registerForm.nombre);
            formData.append('email', registerForm.email);
            formData.append('contrasena', registerForm.contrasena);
            formData.append('rol', 'cliente');

            const response = await fetch(`${baseURL}/registroPost.php`, {
                method: 'POST',
                body: formData,
            });
            const data = await response.json();

            if (data.error) {
                showFeedback('error', data.error);
                return;
            }

            showFeedback('success', data.mensaje || 'Cuenta creada. Revisa tu correo para confirmarla.');
            setLoginEmail(registerForm.email);
            setRegisterForm(initialRegisterState);
            setActiveTab('login');
        } catch (error) {
            console.error('Error al crear cuenta de cliente:', error);
            showFeedback('error', 'No se pudo crear la cuenta.');
        }
    };

    const handleProfileSave = async (event) => {
        event.preventDefault();

        try {
            const response = await fetch(`${baseURL}/customerProfilePut.php`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(profileForm),
            });
            const data = await response.json();

            if (data.error) {
                showFeedback('error', data.error);
                return;
            }

            showFeedback('success', data.mensaje || 'Datos guardados');
            await loadSessionUser();
        } catch (error) {
            console.error('Error al guardar perfil del cliente:', error);
            showFeedback('error', 'No se pudo guardar tu perfil.');
        }
    };

    const handleLogout = async () => {
        try {
            const response = await fetch(`${baseURL}/logout.php`, {
                method: 'POST',
            });
            const data = await response.json();

            if (data.error) {
                showFeedback('error', data.error);
                return;
            }

            showFeedback('success', data.mensaje || 'Sesion cerrada');
            setUser(null);
            setLoginEmail('');
            setLoginPassword('');
            setProfileForm({
                nombre: '',
                telefono: '',
                direccion: '',
            });
        } catch (error) {
            console.error('Error al cerrar sesion del cliente:', error);
            showFeedback('error', 'No se pudo cerrar sesion.');
        }
    };

    return (
        <section className='miCuentaPage'>
            <div className='miCuentaShell'>
                <div className='miCuentaHero'>
                    <span className='miCuentaEyebrow'>{storeName || 'Tienda'}</span>
                    <h1>Mi cuenta</h1>
                    <p>Crea tu cuenta y compra más rápido. Tus datos y pedidos quedarán guardados para que volver a comprar sea mucho más simple.</p>
                </div>

                {feedback.text && (
                    <div className={`miCuentaFeedback ${feedback.type}`}>
                        {feedback.text}
                    </div>
                )}

                {loading ? (
                    <div className='miCuentaCard'>
                        <p>Cargando...</p>
                    </div>
                ) : user ? (
                    <div className='miCuentaGrid'>
                        <article className='miCuentaCard'>
                            <h2>Tus datos</h2>
                            <form className='miCuentaForm' onSubmit={handleProfileSave}>
                                <label>
                                    <span>Nombre</span>
                                    <input
                                        type='text'
                                        value={profileForm.nombre}
                                        onChange={(event) => setProfileForm((prev) => ({ ...prev, nombre: event.target.value }))}
                                    />
                                </label>
                                <label>
                                    <span>Email</span>
                                    <input type='email' value={user.email || ''} disabled />
                                </label>
                                <label>
                                    <span>Telefono</span>
                                    <input
                                        type='text'
                                        value={profileForm.telefono}
                                        onChange={(event) => setProfileForm((prev) => ({ ...prev, telefono: event.target.value }))}
                                    />
                                </label>
                                <label>
                                    <span>Direccion</span>
                                    <input
                                        type='text'
                                        value={profileForm.direccion}
                                        onChange={(event) => setProfileForm((prev) => ({ ...prev, direccion: event.target.value }))}
                                        placeholder='Opcional, para entregas'
                                    />
                                </label>
                                <div className='miCuentaActions'>
                                    <button type='submit' className='miCuentaPrimary'>Guardar datos</button>
                                    <button type='button' className='miCuentaSecondary' onClick={handleLogout}>Cerrar sesion</button>
                                </div>
                            </form>
                        </article>

                        <article className='miCuentaCard'>
                            <h2>Mis pedidos</h2>
                            {customerOrders.length > 0 ? (
                                <div className='miCuentaOrdersScrollBox'>
                                    <div className='miCuentaOrdersList miCuentaOrdersListCompact'>
                                        {customerOrders.map((pedido) => (
                                            <div className='miCuentaOrderCard' key={pedido.idPedido}>
                                                <div className='miCuentaOrderHeader'>
                                                    <strong>Pedido #{pedido.idPedido}</strong>
                                                    <span>{getOrderStatusLabel(pedido.estado)}</span>
                                                </div>
                                                <div className='miCuentaOrderProducts'>
                                                    {getOrderProducts(pedido).map((producto, index) => (
                                                        <div className='miCuentaOrderProductRow' key={`${pedido.idPedido}-${index}`}>
                                                            <span>{producto.titulo}{producto.item ? ` - ${producto.item}` : ''}</span>
                                                            <strong>x{producto.cantidad}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                                <strong className='miCuentaOrderTotal'>Total: {formatMoney(pedido.total)}</strong>
                                                <p>{pedido.entrega}</p>
                                                <small>{pedido.createdAt}</small>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <p>Tus pedidos aparecerán aquí cuando la tienda los marque como pagados, enviados, recibidos o entregados.</p>
                            )}
                            <div className='miCuentaLinks'>
                                <Anchor className='miCuentaCtaLink' to='/productos'>Ir a comprar</Anchor>
                            </div>
                        </article>
                    </div>
                ) : (
                    <div className='miCuentaGrid'>
                        <article className='miCuentaCard'>
                            <div className='miCuentaTabs'>
                                <button type='button' className={activeTab === 'login' ? 'active' : ''} onClick={() => {
                                    setActiveTab('login');
                                    setFeedback({ type: '', text: '' });
                                }}>Iniciar sesion</button>
                                <button type='button' className={activeTab === 'register' ? 'active' : ''} onClick={() => {
                                    setActiveTab('register');
                                    setFeedback({ type: '', text: '' });
                                }}>Crear cuenta</button>
                            </div>

                            {activeTab === 'login' ? (
                                <form className='miCuentaForm' onSubmit={handleCustomerLogin}>
                                    <h2>Accede a tu cuenta</h2>
                                    <label>
                                        <span>Email</span>
                                        <input type='email' value={loginEmail} onChange={(event) => setLoginEmail(event.target.value)} required />
                                    </label>
                                    <label>
                                        <span>Contraseña</span>
                                        <input type='password' value={loginPassword} onChange={(event) => setLoginPassword(event.target.value)} required />
                                    </label>
                                    <button type='submit' className='miCuentaPrimary'>Entrar</button>
                                </form>
                            ) : (
                                <form className='miCuentaForm' onSubmit={handleCustomerRegister}>
                                    <h2>Crea tu cuenta</h2>
                                    <p className='miCuentaFormIntro'>Compra más rápido, guarda tus datos y revisa tus pedidos desde un solo lugar. Te enviaremos un correo para confirmar tu cuenta.</p>
                                    <label>
                                        <span>Nombre</span>
                                        <input type='text' value={registerForm.nombre} onChange={(event) => setRegisterForm((prev) => ({ ...prev, nombre: event.target.value }))} required />
                                    </label>
                                    <label>
                                        <span>Email</span>
                                        <input type='email' value={registerForm.email} onChange={(event) => setRegisterForm((prev) => ({ ...prev, email: event.target.value }))} required />
                                    </label>
                                    <label>
                                        <span>Contraseña</span>
                                        <input type='password' value={registerForm.contrasena} onChange={(event) => setRegisterForm((prev) => ({ ...prev, contrasena: event.target.value }))} required />
                                    </label>
                                    <button type='submit' className='miCuentaPrimary'>Crear cuenta</button>
                                </form>
                            )}
                        </article>
                    </div>
                )}
            </div>
        </section>
    );
}
